import { useUploadProgress } from '../context/UploadProgressContext';
import {
  sendEOREReportWithProgress,
  sendEOREFilesWithProgress,
  createEOREReportWithProgress
} from '../utils/UploadWithProgress';
import { toast } from 'react-toastify';

export const useEOREReportUpload = () => {
  const { startUpload, updateProgress, finishUpload, setError } = useUploadProgress();

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const isProduction = process.env.NODE_ENV === 'production';

  const log = (...args) => {
    if (!isProduction) {
      console.log(...args);
    }
  };

  const simulateStepProgress = async (step, stepName, totalSteps, basePercentage = 0) => {
    const stepRange = 100 / totalSteps;
    const stepStart = (step - 1) * stepRange + basePercentage;

    log('🎬 Starting manual simulation for:', stepName, 'step:', step);

    updateProgress(step, stepName, Math.round(stepStart + 5));
    log('📈 Manual progress:', Math.round(stepStart + 5), '%');
    await delay(150);

    updateProgress(step, stepName, Math.round(stepStart + 15));
    log('📈 Manual progress:', Math.round(stepStart + 15), '%');
    await delay(200);

    updateProgress(step, stepName, Math.round(stepStart + 25));
    log('📈 Manual progress:', Math.round(stepStart + 25), '%');
    await delay(150);

    log('✅ Manual simulation completed for:', stepName);
  };

  const uploadEOREReport = async (formData, photos, attachments, options = {}) => {
    const {
      onSuccess,
      onError,
      skipPhotos = false,
      skipAttachments = false,
      isEdit = false,
      reportId = null
    } = options;

    try {
      let totalSteps = 1;
      if (!skipPhotos && photos.length > 0) totalSteps++;
      if (!skipAttachments && attachments.length > 0) totalSteps++;

      const isDraft = formData.draft;
      const actionText = isEdit
        ? (isDraft ? 'Saving EORE report...' : 'Sending EORE report...')
        : (isDraft ? 'Creating EORE draft...' : 'Creating EORE report...');

      log('🚀 Starting EORE upload with', totalSteps, 'steps');
      startUpload(totalSteps, actionText);

      let step = 0;
      let resultData = null;

      step++;
      log('📋 Step', step, ': Starting EORE report', isEdit ? 'update' : 'creation');
      await simulateStepProgress(step, isEdit ? 'Saving EORE report data...' : 'Creating EORE report...', totalSteps);

      const response = isEdit
        ? await sendEOREReportWithProgress(formData, reportId, (percentage) => {
            const stepRange = 100 / totalSteps;
            const stepStart = (step - 1) * stepRange;
            const stepProgress = Math.min(stepStart + (percentage * stepRange / 100), stepRange * step);
            log('📊 Real progress callback:', percentage, '→', Math.round(stepProgress));
            updateProgress(step, 'Saving EORE report data...', Math.round(stepProgress));
          })
        : await createEOREReportWithProgress(formData, (percentage) => {
            const stepRange = 100 / totalSteps;
            const stepStart = (step - 1) * stepRange;
            const stepProgress = Math.min(stepStart + (percentage * stepRange / 100), stepRange * step);
            log('📊 Real progress callback:', percentage, '→', Math.round(stepProgress));
            updateProgress(step, 'Creating EORE report...', Math.round(stepProgress));
          });

      if ((isEdit && response.status === 200) || (!isEdit && response.status === 201)) {
        resultData = response.data;
        const stepComplete = Math.round((step / totalSteps) * 100);
        updateProgress(step, isEdit ? 'EORE report data saved' : 'EORE report created', stepComplete);
        log('✅ EORE report', isEdit ? 'saved' : 'created', ', progress:', stepComplete, '%');
      }

      const finalReportId = isEdit ? reportId : resultData.id;

      if (!skipPhotos && photos.length > 0) {
        step++;
        log('📷 Step', step, ': Starting photos upload');
        await simulateStepProgress(step, `Uploading ${photos.length} photos...`, totalSteps);

        await sendEOREFilesWithProgress(photos, finalReportId, 'images', (percentage) => {
          const stepRange = 100 / totalSteps;
          const stepStartPos = (step - 1) * stepRange;
          const stepProgress = Math.min(stepStartPos + (percentage * stepRange / 100), stepRange * step);
          updateProgress(step, `Uploading photos...`, Math.round(stepProgress));
        });

        const stepComplete = Math.round((step / totalSteps) * 100);
        updateProgress(step, 'Photos uploaded', stepComplete);
      }

      if (!skipAttachments && attachments.length > 0) {
        step++;
        log('📎 Step', step, ': Starting attachments upload');
        await simulateStepProgress(step, `Uploading ${attachments.length} files...`, totalSteps);

        await sendEOREFilesWithProgress(attachments, finalReportId, 'files', (percentage) => {
          const stepRange = 100 / totalSteps;
          const stepStartPos = (step - 1) * stepRange;
          const stepProgress = Math.min(stepStartPos + (percentage * stepRange / 100), stepRange * step);
          updateProgress(step, `Uploading files...`, Math.round(stepProgress));
        });

        const stepComplete = Math.round((step / totalSteps) * 100);
        updateProgress(step, 'Files uploaded', stepComplete);
      }

      log('🏁 All EORE uploads completed, finishing...');
      updateProgress(step, 'Completing...', 95);
      await delay(300);
      updateProgress(step, 'Completed', 100);

      finishUpload();

      if (isEdit) {
        toast.success('Photos and attachments updated successfully');
      } else {
        toast.success('EORE report created successfully');
      }

      if (onSuccess) {
        onSuccess(resultData);
      }

      return resultData;

    } catch (error) {
      console.error('❌ EORE Upload failed:', error);
      let errorMessage = 'Failed to upload EORE report';

      if (error.response?.status === 400) {
        errorMessage = 'Validation error. Please check your data.';
      } else if (error.response?.status === 413) {
        errorMessage = 'Files are too large. Please reduce file sizes.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);

      if (onError) {
        onError(error);
      }

      throw error;
    }
  };

  return {
    uploadEOREReport,
  };
};